
import { useState, useCallback, useEffect, useRef } from "react";

export const useMediaRecorder = () => {
  const [mediaUrl, setMediaUrl] = useState(null);
  const [recordingState, setRecordingState] = useState("idle");
  const [error, setError] = useState(null);
  const [analyser, setAnalyser] = useState(null);
  const [link, setLink] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const streamRef = useRef(null);
  const mediaUrlRef = useRef(null);
  const recordingStateRef = useRef("idle");

  const updateRecordingState = useCallback((state) => {
    recordingStateRef.current = state;
    setRecordingState(state);
  }, []);

  const cleanupAudio = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (audioContextRef.current) {
      const context = audioContextRef.current;
      audioContextRef.current = null;
      if (context.state !== "closed") context.close().catch(console.error);
    }

    setAnalyser(null);
  }, []);

  const createMediaBlob = useCallback((chunks, mimeType) => {
    const mediaBlob = new Blob(chunks, { type: mimeType });
    const url = URL.createObjectURL(mediaBlob);

    if (mediaUrlRef.current) URL.revokeObjectURL(mediaUrlRef.current);

    mediaUrlRef.current = url;
    setLink(mediaBlob);
    setMediaUrl(url);
    updateRecordingState("stopped");
  }, [updateRecordingState]);

  const startRecording = useCallback(async () => {
    if (recordingStateRef.current !== "idle") return;

    updateRecordingState("starting");
    setError(null);

    try {
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") throw new Error("Audio recording is not supported in this browser.");

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const AudioContextClass = window.AudioContext || window.webkitAudioContext;

      if (AudioContextClass) {
        const audioContext = new AudioContextClass();
        audioContextRef.current = audioContext;

        const source = audioContext.createMediaStreamSource(stream);
        const analyserNode = audioContext.createAnalyser();

        source.connect(analyserNode);
        setAnalyser(analyserNode);

        if (audioContext.state === "suspended") await audioContext.resume();
      }

      const supportedTypes = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
      const mimeType = supportedTypes.find((type) => MediaRecorder.isTypeSupported(type));
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

      mediaRecorderRef.current = recorder;

      const chunks = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };

      recorder.onstop = () => {
        if (chunks.length > 0) createMediaBlob(chunks, recorder.mimeType || mimeType || "audio/webm");
        else updateRecordingState("idle");

        cleanupAudio();
        mediaRecorderRef.current = null;
      };

      recorder.onerror = (event) => {
        console.error("Recording error:", event);
        setError("An error occurred while recording.");
        updateRecordingState("idle");
        cleanupAudio();
      };

      recorder.start();
      updateRecordingState("recording");
    } catch (err) {
      console.error("Recording error:", err);
      setError(err?.message === "Audio recording is not supported in this browser." ? err.message : "Failed to start recording. Please allow microphone access.");
      updateRecordingState("idle");
      cleanupAudio();
    }
  }, [cleanupAudio, createMediaBlob, updateRecordingState]);

  const stopRecording = useCallback(() => {
    if (recordingStateRef.current !== "recording" || mediaRecorderRef.current?.state !== "recording") return;

    updateRecordingState("stopping");
    mediaRecorderRef.current.stop();
  }, [updateRecordingState]);

  const deleteRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();

    if (mediaUrlRef.current) {
      URL.revokeObjectURL(mediaUrlRef.current);
      mediaUrlRef.current = null;
    }

    setMediaUrl(null);
    setLink(null);
    setError(null);
    updateRecordingState("idle");
    cleanupAudio();
  }, [cleanupAudio, updateRecordingState]);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
      if (mediaUrlRef.current) URL.revokeObjectURL(mediaUrlRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((track) => track.stop());
      if (audioContextRef.current?.state !== "closed") audioContextRef.current?.close().catch(console.error);
    };
  }, []);

  return { mediaUrl, recordingState, link, startRecording, stopRecording, deleteRecording, error, analyser };
};
